import { useState, useEffect } from 'react';
import db from '@/db';
import {
  previewReceipt,
  connectPrinter, disconnectPrinter, hasConnectedPrinter,
} from '@/lib/printer';
import { getPrintMode, setPrintMode, printTest } from '@/lib/printSmart';
import { Printer, FileText, Plug, Unplug, TestTube2, Monitor } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export default function PrintPage() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [preview, setPreview] = useState('');
  const [printerConnected, setPrinterConnected] = useState(hasConnectedPrinter());
  const [printMode, setPrintModeState] = useState('windows');

  useEffect(() => {
    const loadMode = async () => {
      const mode = await getPrintMode();
      setPrintModeState(mode);
    };
    loadMode();
    const iv = setInterval(() => setPrinterConnected(hasConnectedPrinter()), 2000);
    return () => clearInterval(iv);
  }, []);

  const handlePrintModeChange = async (mode) => {
    try {
      await setPrintMode(mode);
      setPrintModeState(mode);
      toast.success(`Modo: ${mode === 'windows' ? 'Sistema Windows' : 'Web Serial (ESC/POS)'}`);
    } catch (err) {
      toast.error(err.message || 'Falha ao salvar modo');
    }
  };

  useEffect(() => {
    const load = async () => {
      const all = await db.mesas.where('status').equals('occupied').toArray();
      setTables(all);
    };
    load();
  }, []);

  const handleTableSelect = async (tableId) => {
    setSelectedTable(tableId);
    const id = Number(tableId);
    const tableOrders = await db.orders.where('tableId').equals(id).toArray();
    const table = await db.mesas.get(id);
    if (table && tableOrders.length > 0) {
      setPreview(previewReceipt(table, tableOrders));
    } else {
      setPreview('');
    }
  };

  const handleConnectPrinter = async () => {
    try {
      if (!('serial' in navigator)) {
        toast.error('Web Serial API não suportada. Use Chrome ou Edge.');
        return;
      }
      await connectPrinter({ interactive: true });
      setPrinterConnected(true);
      toast.success('Jetway conectada!', {
        description: 'Porta salva — próximas impressões dispensam o prompt.',
      });
    } catch (err) {
      // Usuário cancelou o prompt ou erro de conexão
      if (err?.name === 'NotFoundError' || /No port selected/i.test(err?.message || '')) {
        toast.message('Seleção cancelada', { description: 'Escolha a porta da Jetway quando o Chrome perguntar.' });
      } else {
        toast.error(err.message || 'Erro ao conectar impressora');
      }
    }
  };

  const handleDisconnectPrinter = async () => {
    await disconnectPrinter();
    setPrinterConnected(false);
    toast.success('Impressora desconectada');
  };

  const handleTestPrint = async () => {
    try {
      const r = await printTest();
      toast.success('Teste enviado para impressão!', {
        description: r.mode === 'windows'
          ? 'Confira o diálogo do sistema e escolha a Jetway JP-500'
          : 'Via Jetway (ESC/POS). Confira o papel.',
      });
    } catch (err) {
      toast.error(err.message || 'Falha no teste de impressão');
    }
  };

  const handlePrintPreview = () => {
    if (!preview) {
      toast.error('Selecione uma mesa com pedidos');
      return;
    }
    // In a real scenario, this would send ESC/POS bytes to the printer
    toast.success('Cupom pronto para impressao!');
  };

  return (
    <div data-testid="print-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-3xl tracking-tight font-bold text-zinc-900">
            Impressao
          </h2>
          <p className="text-sm text-zinc-500 mt-1">Jetway JP-500 · Modo {printMode === 'windows' ? 'Sistema Windows' : 'Web Serial'}</p>
        </div>
        <Badge
          className={
            printMode === 'windows'
              ? 'bg-blue-100 text-blue-700 border-blue-200'
              : printerConnected
                ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                : 'bg-zinc-100 text-zinc-500 border-zinc-200'
          }
        >
          {printMode === 'windows' ? 'Via Sistema' : printerConnected ? 'Conectada' : 'Desconectada'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Controls */}
        <div className="space-y-6">
          {/* Fase 22 — Seleção de Modo de Impressão */}
          <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 space-y-3">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">
              Modo de Impressão
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="print-mode-windows"
                onClick={() => handlePrintModeChange('windows')}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                  printMode === 'windows'
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400'
                }`}
              >
                <Monitor size={20} strokeWidth={2} />
                <span className="text-xs font-bold">Sistema Windows</span>
                <span className="text-[10px] text-zinc-500 text-center leading-tight">Usa driver Jetway instalado</span>
              </button>
              <button
                data-testid="print-mode-webserial"
                onClick={() => handlePrintModeChange('webserial')}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                  printMode === 'webserial'
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400'
                }`}
              >
                <Plug size={20} strokeWidth={2} />
                <span className="text-xs font-bold">Web Serial</span>
                <span className="text-[10px] text-zinc-500 text-center leading-tight">ESC/POS via porta COM</span>
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              {printMode === 'windows' ? (
                <><strong>Sistema Windows:</strong> abre o diálogo padrão de impressão. Escolha a <strong>Jetway JP-500</strong> na lista. Funciona com o driver Jetway que você já tem instalado.</>
              ) : (
                <><strong>Web Serial:</strong> envia bytes ESC/POS crus direto na porta COM. Só funciona se a Jetway estiver instalada como porta serial virtual (driver CH340/FTDI).</>
              )}
            </p>
          </div>

          {/* Printer Connection (só relevante no modo Web Serial) */}
          {printMode === 'webserial' && (
          <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 space-y-3">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">
              Conexão Web Serial
            </p>
            <button
              data-testid="connect-printer-btn"
              onClick={handleConnectPrinter}
              className="w-full flex items-center justify-center gap-2 bg-white text-zinc-900 rounded-lg font-bold border-2 border-zinc-300 hover:border-zinc-900 active:translate-y-[2px] transition-all py-4 min-h-[48px]"
            >
              <Plug size={18} />
              {printerConnected ? 'Reconectar Jetway' : 'Conectar Jetway'}
            </button>
            {printerConnected && (
              <button
                data-testid="disconnect-printer-btn"
                onClick={handleDisconnectPrinter}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 py-1"
              >
                <Unplug size={12} /> Desconectar
              </button>
            )}
          </div>
          )}

          {/* Teste de impressão (sempre visível) */}
          <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">
              Teste
            </p>
            <button
              data-testid="test-print-btn"
              onClick={handleTestPrint}
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-lg font-bold border-2 border-transparent hover:bg-emerald-700 active:translate-y-[2px] transition-all py-4 min-h-[48px]"
            >
              <TestTube2 size={18} />
              Teste de Impressão
            </button>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              {printMode === 'windows'
                ? 'Vai abrir o diálogo de impressão do Windows — escolha a Jetway JP-500.'
                : 'Vai abrir o prompt de porta serial na 1ª vez — depois fica salvo.'}
            </p>
          </div>

          {/* Table Selection */}
          <div className="bg-white border-2 border-zinc-200 rounded-xl p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-4">
              Selecionar Mesa
            </p>
            <Select value={selectedTable} onValueChange={handleTableSelect}>
              <SelectTrigger data-testid="print-table-select" className="h-12 border-2 border-zinc-200 rounded-xl text-base">
                <SelectValue placeholder="Selecione uma mesa ocupada" />
              </SelectTrigger>
              <SelectContent>
                {tables.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    Mesa {String(t.number).padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tables.length === 0 && (
              <p className="text-sm text-zinc-400 mt-2">Nenhuma mesa ocupada</p>
            )}
          </div>

          {/* Actions */}
          <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 space-y-3">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-4">
              Acoes
            </p>
            <button
              data-testid="print-conference-btn"
              onClick={handlePrintPreview}
              disabled={!preview}
              className="w-full flex items-center justify-center gap-2 bg-[#2563eb] text-white rounded-lg font-bold border-2 border-transparent hover:bg-[#1d4ed8] active:translate-y-[2px] transition-all py-4 min-h-[48px] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Printer size={18} />
              Imprimir Conferencia
            </button>
            <button
              data-testid="print-comanda-btn"
              disabled={!preview}
              onClick={() => toast.success('Comanda pronta para impressao!')}
              className="w-full flex items-center justify-center gap-2 bg-white text-zinc-900 rounded-lg font-bold border-2 border-zinc-300 hover:border-zinc-900 active:translate-y-[2px] transition-all py-4 min-h-[48px] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileText size={18} />
              Imprimir Comanda
            </button>
          </div>
        </div>

        {/* Receipt Preview */}
        <div className="bg-white border-2 border-zinc-200 rounded-xl p-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-4">
            Preview do Cupom
          </p>
          {preview ? (
            <pre
              data-testid="print-receipt-preview"
              className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 font-mono text-xs whitespace-pre overflow-x-auto max-h-[500px] overflow-y-auto leading-relaxed"
            >
              {preview}
            </pre>
          ) : (
            <div className="text-center py-20 text-zinc-300">
              <FileText size={48} className="mx-auto mb-4" />
              <p className="text-sm">Selecione uma mesa para visualizar o cupom</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
